import { editableLines, lineInputs, InvoiceLinesEditor, InvoiceLineTotals } from '../../components/InvoiceLines';
import { linesSubtotalExCents, validateInvoiceLines } from '../../../shared/invoiceLines';
import type { InvoiceDraftResponse } from '../../../shared/invoicing';
import { InvoiceKindChip, InvoiceKindSelect } from '../../components/InvoiceKind';
import { addDaysIso } from '../../../shared/taskBuckets';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { deriveInvoiceNumber, grossFromEx, gstOnEx } from '../../../shared/finance';
import { INVOICE_STATUSES, type InvoiceKind, type ClientWithCounts, type InvoiceStatus, type InvoiceWithNames, type ProjectWithClient } from '../../../shared/types';
import { DateField, formatDayMonth, formatInvoiceNumber, formatMoneyExact, Modal, Pill, parseMoney, SelectField, Seg } from '../../components/primitives';
import { invoiceNumberWarning } from '../../lib/invoiceNumbers';
import { useIsMobile } from '../../lib/useIsMobile';
import { formatProjectNumber } from '../../lib/calendar';
import { INVOICE_STATUS_LABELS, rawInvoiceNumber } from './helpers';


// ── New invoice modal (09-new-invoice) ────────────────────────────────────

export function NewInvoiceModal({
  defaultClientId,
  draftDefaults,
  onClose,
  onCreated,
}: {
  defaultClientId?: string;
  draftDefaults?: InvoiceDraftResponse;
  onClose: () => void;
  onCreated: () => void;
}) {
  const isMobile = useIsMobile();
  const [clients, setClients] = useState<ClientWithCounts[]>([]);
  const [projects, setProjects] = useState<ProjectWithClient[]>([]);
  const [invoices, setInvoices] = useState<InvoiceWithNames[]>([]);
  const [form, setForm] = useState({
    invoice_number: draftDefaults?.draft ? formatInvoiceNumber(draftDefaults.draft.invoice_number) : '',
    invoice_kind: (draftDefaults?.draft?.kind ?? null) as InvoiceKind | null,
    description: draftDefaults?.draft?.description ?? '',
    lines: editableLines(draftDefaults?.draft?.lines ?? []),
    status: 'draft' as InvoiceStatus,
    client_id: (draftDefaults?.project.client_id ?? defaultClientId ?? null) as string | null,
    project_id: (draftDefaults?.project.id ?? null) as string | null,
    amount: draftDefaults?.draft ? (draftDefaults.draft.ex_cents / 100).toFixed(2) : '',
    gstMode: 'incl' as 'incl' | 'free',
    issue_date: null as string | null,
    due_date: (draftDefaults?.draft?.due_date ?? null) as string | null,
    external_url: '',
    notes: '',
  });
  const [defaultTerms, setDefaultTerms] = useState<number | null>(null);
  const selectedClient = clients.find((client) => client.id === form.client_id);
  const [projectDefaults, setProjectDefaults] = useState(draftDefaults);
  const touched = useRef(new Set<keyof typeof form>());
  const [draftLoading, setDraftLoading] = useState(false);
  const fromProject = projectDefaults && form.project_id === projectDefaults.project.id && form.client_id === projectDefaults.project.client_id ? projectDefaults : undefined;
  const termsDays = fromProject?.terms.days ?? selectedClient?.payment_terms_days ?? defaultTerms;
  const termsSource = fromProject?.terms.source ?? (selectedClient?.payment_terms_days != null ? 'client' : 'default');

  const [dueTouched, setDueTouched] = useState(false);
  // Follow the issue date until Henry edits or clears the due date.
  // Clearing or manually changing the due date alone never triggers a refill.
  useEffect(() => {
    if (!selectedClient || termsDays === null || dueTouched) return;
    setForm((f) => f.issue_date
      ? { ...f, due_date: addDaysIso(f.issue_date, termsDays) } : f);
  }, [selectedClient, termsDays, form.issue_date, dueTouched]);

  // Stop auto-defaulting the number once Henry types in the field himself.
  const [numberTouched, setNumberTouched] = useState(false);
  const [duplicateOf, setDuplicateOf] = useState<{ number: string; warning: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ invoice_default_terms_days: number }>('/api/admin/system/prefs')
      .then((d) => setDefaultTerms(d.invoice_default_terms_days))
      .catch(() => {});
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((d) => setClients(d.clients))
      .catch(() => {});
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((d) => setProjects(d.projects))
      .catch(() => {});
    api<{ invoices: InvoiceWithNames[] }>('/api/admin/finance/invoices')
      .then((d) => setInvoices(d.invoices))
      .catch(() => {});
  }, []);

  // A prefilled modal follows its current project, but manual edits belong to
  // Henry. Read touched fields when the response arrives, not when it starts.
  useEffect(() => {
    if (!draftDefaults) return;
    if (form.project_id === projectDefaults?.project.id && form.client_id === projectDefaults.project.client_id) return;
    let cancelled = false;
    const applyDefaults = (data?: InvoiceDraftResponse) => {
      const draft = data?.draft;
      const values = {
        invoice_number: draft ? formatInvoiceNumber(draft.invoice_number) : '',
        amount: draft ? (draft.ex_cents / 100).toFixed(2) : '',
        description: draft?.description ?? '',
        lines: editableLines(draft?.lines ?? []),
        invoice_kind: draft?.kind ?? null,
        due_date: draft?.due_date ?? null,
      };
      setForm(f => ({ ...f, ...Object.fromEntries(
        Object.entries(values).filter(([key]) => !touched.current.has(key as keyof typeof form)),
      ) }));
    };
    setProjectDefaults(undefined);
    applyDefaults();
    setError(null);
    if (!form.project_id) {
      setDraftLoading(false);
      return;
    }
    setDraftLoading(true);
    api<InvoiceDraftResponse>(`/api/admin/projects/${form.project_id}/invoice-draft`)
      .then(data => {
        if (cancelled) return;
        setProjectDefaults(data);
        applyDefaults(data);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load invoice defaults.');
      })
      .finally(() => { if (!cancelled) setDraftLoading(false); });
    return () => { cancelled = true; };
    // Only picker changes refresh defaults; applying a response must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project_id, form.client_id]);

  // Voided projects never take new invoices (the API rejects them), so they
  // don't belong in the picker at all.
  const liveProjects = projects.filter((p) => p.voided_at === null);
  const clientProjects = form.client_id
    ? liveProjects.filter((p) => p.client_id === form.client_id)
    : liveProjects;

  const pickProject = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    const next = { ...form, project_id: projectId };
    if (project && !form.client_id) next.client_id = project.client_id;
    if (project && !numberTouched) {
      const derived = deriveInvoiceNumber(
        project.project_number,
        invoices.filter((i) => i.project_id === projectId).map((i) => i.invoice_number),
      );
      if (derived) next.invoice_number = formatInvoiceNumber(derived);
    }
    setForm(next);
  };

  // Henry quotes ex GST — the amount field takes the ex figure and GST goes
  // on top (storage stays inc-GST: amount_cents incl. GST + gst_cents).
  const inputs = lineInputs(form.lines);
  const linesError = validateInvoiceLines(inputs);
  const exCents = form.lines.length ? linesSubtotalExCents(inputs) : parseMoney(form.amount);
  const gstCents = exCents === null ? null : !form.lines.length && form.gstMode === 'free' ? 0 : gstOnEx(exCents);
  const totalCents = exCents === null ? null : !form.lines.length && form.gstMode === 'free' ? exCents : grossFromEx(exCents);

  const submit = async () => {
    if (busyRef.current || draftLoading) return;
    if (linesError) { setError(linesError); return; }
    const number = rawInvoiceNumber(form.invoice_number);
    if (!form.client_id || !number || totalCents === null) return;
    // Latch synchronously before the duplicate-number preflight. React state
    // disables the button on render; this local guard also closes the same-
    // tick double-click window before any awaited work begins.
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      // Warn-only duplicate gate (numbers aren't unique): the first click on
      // a taken number raises the warning, a second one creates it anyway.
      if (duplicateOf?.number !== number) {
        const warning = await invoiceNumberWarning(number);
        if (warning) {
          setDuplicateOf({ number, warning });
          return;
        }
      }
      await api('/api/admin/finance/invoices', {
        method: 'POST',
        json: {
          client_id: form.client_id,
          project_id: form.project_id,
          invoice_number: number,
          amount_cents: totalCents,
          ...(form.lines.length || touched.current.has('lines') ? { lines: inputs } : {}),
          gst_cents: gstCents,
          status: form.status,
          ...(form.invoice_kind !== null || touched.current.has('invoice_kind') ? { invoice_kind: form.invoice_kind } : {}),
          description: form.description.trim() || null,
          issue_date: form.issue_date,
          due_date: form.due_date || undefined,
          external_url: form.external_url || null,
          notes: form.notes || null,
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // ── Field bodies, shared by both widths ────────────────────────────────
  // Only the order and the wrappers differ at ≤880px (§8.2 D): money leads,
  // the number/status and date pairs sit side by side, and GST becomes a
  // segmented control. The state and submit above are the same either way.
  const numberField = (
    <>
      <span className="f-label">Invoice number</span>
      <input
        className="f-input mono"
        value={form.invoice_number}
        placeholder="#0241"
        inputMode={isMobile ? 'numeric' : undefined}
        onChange={(e) => {
          touched.current.add('invoice_number');
          setNumberTouched(true);
          setForm((f) => ({ ...f, invoice_number: e.target.value }));
        }}
      />
      {duplicateOf?.number === rawInvoiceNumber(form.invoice_number) && (
        <span className="number-warning">{duplicateOf.warning}</span>
      )}
    </>
  );
  const statusField = (
    <>
      <span className="f-label">Status</span>
      <SelectField
        label="Status"
        items={INVOICE_STATUSES.map((s) => ({ value: s, label: INVOICE_STATUS_LABELS[s] }))}
        value={form.status}
        onChange={(status) => setForm((f) => ({ ...f, status }))}
      />
    </>
  );
  const clientField = (
    <>
      <span className="f-label">
        Client <span className="req">*</span>
      </span>
      <SelectField
        label="Client"
        items={clients.map((c) => ({ value: c.id, label: c.name }))}
        value={form.client_id}
        onChange={(client_id) =>
          setForm((f) => ({
            ...f,
            client_id,
            // Changing client invalidates a project that belongs elsewhere.
            project_id:
              f.project_id && projects.find((p) => p.id === f.project_id)?.client_id !== client_id
                ? null
                : f.project_id,
          }))
        }
        placeholder="Select client…"
        searchable
      />
    </>
  );
  const projectField = (
    <>
      <div className="f-label-row"><span className="f-label">Project</span><span className="f-help">Brand follows project</span></div>
      <SelectField
        label="Project"
        items={clientProjects.map((p) => ({
          value: p.id,
          // The phone shows the number too — "#0246 · Colour of the Year launch".
          label: isMobile ? [formatProjectNumber(p.project_number), p.name].filter(Boolean).join(' · ') : p.name,
        }))}
        value={form.project_id}
        onChange={pickProject}
        placeholder="Link to project…"
        searchable
      />
      {form.project_id && <Pill>{projects.find(p => p.id === form.project_id)?.brand ?? fromProject?.project.brand}</Pill>}
    </>
  );
  const amountField = (
    <>
      <span className="f-label">
        Amount · ex GST <span className="req">*</span>
      </span>
      <input
        className={isMobile ? 'f-input money inv-amount' : 'f-input money'}
        value={form.amount}
        placeholder="$0.00"
        inputMode={isMobile ? 'decimal' : undefined}
        onChange={(e) => { touched.current.add('amount'); setForm((f) => ({ ...f, amount: e.target.value })); }}
        autoFocus={!isMobile}
      />
      {totalCents !== null && form.gstMode === 'incl' && (
        <span className="f-help">Total incl. GST {formatMoneyExact(totalCents)}</span>
      )}
    </>
  );
  const gstOptions = [
    {
      value: 'incl' as const,
      label: `+ 10% on top${gstCents !== null && form.gstMode === 'incl' ? ` · ${formatMoneyExact(gstCents)}` : ''}`,
    },
    { value: 'free' as const, label: 'GST-free' },
  ];
  const gstField = (
    <>
      <span className="f-label">GST</span>
      {isMobile ? (
        <Seg options={gstOptions} value={form.gstMode} onChange={(gstMode) => setForm((f) => ({ ...f, gstMode }))} />
      ) : (
        <SelectField
          items={gstOptions}
          value={form.gstMode}
          onChange={(gstMode) => setForm((f) => ({ ...f, gstMode }))}
          mono
        />
      )}
    </>
  );
  const issueField = (
    <>
      <div className="f-label-row"><span className="f-label">Issue date</span><span className="f-help">Set when you send</span></div>
      <DateField
        label="Issue date"
        placeholder="Blank until sent"
        value={form.issue_date}
        onChange={(issue_date) => setForm((f) => ({ ...f, issue_date }))}
      />
    </>
  );
  const dueField = (
    <>
      <span className="f-label">Due date</span>
      <DateField label="Due date" value={form.due_date} onChange={(due_date) => { touched.current.add('due_date'); setDueTouched(true); setForm((f) => ({ ...f, due_date })); }} />
      {termsDays !== null && <span className="f-help">Due {termsDays} days after issue ({termsSource} terms)</span>}
    </>
  );
  const descriptionField = <><span className="f-label">Description</span><input className="f-input" aria-label="Description" value={form.description}
    onChange={e => { touched.current.add('description'); setForm(f => ({ ...f, description: e.target.value })); }} /><span className="f-help">Shown to the client</span></>;
  const kindField = <><span className="f-label">Kind</span><InvoiceKindSelect value={form.invoice_kind}
    onChange={invoice_kind => { touched.current.add('invoice_kind'); setForm(f => ({ ...f, invoice_kind })); }} /></>;
  const linkField = (
    <>
      <span className="f-label">External link</span>
      <input
        className="f-input mono"
        value={form.external_url}
        placeholder="Xero / Stripe URL (optional)"
        inputMode={isMobile ? 'url' : undefined}
        onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))}
      />
    </>
  );
  const notesField = (
    <>
      <span className="f-label">Notes</span>
      <textarea
        className="f-textarea"
        value={form.notes}
        placeholder="What this invoice covers…"
        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
      />
    </>
  );

  return (
    <Modal
      mobile="full"
      title="New invoice"
      width={form.lines.length ? 720 : 620}
      context={isMobile ? "Drafts don't count as outstanding" : 'FINANCE'}
      onClose={onClose}
      hint={isMobile ? undefined : "Drafts don't count as outstanding"}
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={busy || draftLoading || !form.client_id || !rawInvoiceNumber(form.invoice_number) || totalCents === null}
          >
            {duplicateOf?.number === rawInvoiceNumber(form.invoice_number)
              ? 'Create anyway →'
              : 'Create invoice →'}
          </button>
        </>
      }
    >
      {fromProject?.draft && <div className="inv-from">
        <InvoiceKindChip kind={form.invoice_kind} />
        <span>{fromProject.invoiced.deposit ? <>
          Deposit of <span className="money">{formatMoneyExact(fromProject.invoiced.deposit.amount_cents)}</span> already invoiced
          {fromProject.invoiced.deposit.issue_date ? ` on ${formatDayMonth(fromProject.invoiced.deposit.issue_date)}` : ' (issue date not set)'}
          {' '}<span className={fromProject.invoiced.deposit.status === 'paid' ? 'ok' : ''}>({fromProject.invoiced.deposit.status})</span>
          <span className="sub">Remaining {formatMoneyExact(fromProject.draft!.ex_cents)} ex GST of the {formatMoneyExact(fromProject.project.value_cents)} quote</span>
        </> : <>No deposit on this project, so this draft is the full quote<span className="sub">{formatMoneyExact(fromProject.draft!.ex_cents)} ex GST</span></>}</span>
      </div>}
      {isMobile ? (
        <div className="form-grid inv-new-form">
          <div className="f-field">{clientField}</div>
          <div className="f-field">{projectField}</div>
          <div className="f-field">{descriptionField}</div>
          <div className="f-field"><InvoiceLinesEditor lines={form.lines} onChange={lines => {
            touched.current.add('lines'); setForm(f => ({ ...f, lines }));
          }} /></div>
          {form.lines.length ? <div className="f-field"><InvoiceLineTotals lines={inputs} /></div> : <>
            <div className="f-field">{amountField}</div><div className="f-field">{gstField}</div>
          </>}
          <div className="f-field inv-pair">{numberField}</div>
          <div className="f-field inv-pair">{statusField}</div>
          <div className="f-field inv-pair">{issueField}</div>
          <div className="f-field inv-pair">{dueField}</div>
          <div className="f-field">{kindField}</div>
          <div className="f-field">{linkField}</div>
          <div className="f-field">{notesField}</div>
          {error && <p className="error-text span-2">{error}</p>}
        </div>
      ) : (
        <div className="form-grid">
          <div className="f-field">{numberField}</div>
          <div className="f-field">{statusField}</div>
          <div className="f-field">{clientField}</div>
          <div className="f-field">{projectField}</div>
          <div className="f-field span-2">{descriptionField}</div>
          <div className="f-field span-2"><InvoiceLinesEditor lines={form.lines} onChange={lines => {
            touched.current.add('lines'); setForm(f => ({ ...f, lines }));
          }} /></div>
          {form.lines.length ? <div className="f-field span-2"><InvoiceLineTotals lines={inputs} /></div> : <>
            <div className="f-field">{amountField}</div><div className="f-field">{gstField}</div>
          </>}
          <div className="f-field">{issueField}</div>
          <div className="f-field">{dueField}</div>
          <div className="f-field span-2">{kindField}</div>
          <div className="f-field span-2">{linkField}</div>
          <div className="f-field span-2">{notesField}</div>
          {error && <p className="error-text span-2">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
